import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, GripVertical, Sparkles } from "lucide-react";
import ImageUpload from "../../components/cms/ImageUpload";

const LANG_TABS = [
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

const TRANSLATABLE_FIELDS = ["title", "subtitle", "cta_label"];

export default function HeroSlidesAdmin() {
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [reordering, setReordering] = useState(false);
  const [tab, setTab] = useState("es");
  const [translating, setTranslating] = useState(false);
  const dragFromRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const load = () =>
    api.get("/admin/hero-slides")
      .then((r) => setSlides([...r.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))))
      .catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = {
    image_url: "",
    title_es: "", title_fr: "", title_en: "", title_ar: "",
    subtitle_es: "", subtitle_fr: "", subtitle_en: "", subtitle_ar: "",
    cta_label_es: "", cta_label_fr: "", cta_label_en: "", cta_label_ar: "",
    cta_url: "", order: slides.length + 1, active: true,
  };

  const openEditor = (s) => { setTab("es"); setEditing(s); };

  const save = async () => {
    setError("");
    try {
      if (editing.id) {
        await api.patch(`/admin/hero-slides/${editing.id}`, editing);
      } else {
        await api.post("/admin/hero-slides", editing);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este slide?")) return;
    await api.delete(`/admin/hero-slides/${id}`);
    load();
  };

  // Fill the current tab's translatable fields from the ES source via the AI
  // translate endpoint. Skips when target is ES or when the ES field is empty.
  const aiTranslateCurrent = async () => {
    if (!editing || tab === "es") return;
    setTranslating(true);
    setError("");
    try {
      for (const f of TRANSLATABLE_FIELDS) {
        const src = editing[`${f}_es`];
        if (!src || !String(src).trim()) continue;
        const { data } = await api.post("/admin/translate", {
          text: src,
          source_lang: "es",
          target_lang: tab,
        });
        if (data?.translated) {
          setEditing((prev) => ({ ...prev, [`${f}_${tab}`]: data.translated }));
        }
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTranslating(false);
    }
  };

  // --- Drag & drop reordering ------------------------------------------------
  const onDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== idx) setDragOver(idx);
  };
  const onDragLeave = () => setDragOver(null);
  const onDrop = (toIdx) => async (e) => {
    e.preventDefault();
    const fromIdx = dragFromRef.current;
    setDragOver(null);
    dragFromRef.current = null;
    if (fromIdx == null || fromIdx === toIdx) return;

    const next = [...slides];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const withOrder = next.map((s, i) => ({ ...s, order: i + 1 }));
    setSlides(withOrder);

    setReordering(true);
    try {
      const changed = withOrder.filter((s, i) => s.order !== slides[i]?.order || s.id !== slides[i]?.id);
      await Promise.all(changed.map((s) => api.patch(`/admin/hero-slides/${s.id}`, { order: s.order })));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid="cms-hero-slides">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">Hero Slides</h1>
          <p className="text-sm text-gray-600">
            Slides editables de la portada. Arrastre <GripVertical className="inline w-3.5 h-3.5 -mt-0.5" /> para reordenar.
            {reordering && <span className="ml-2 text-[#C8102E]">Guardando orden…</span>}
          </p>
        </div>
        <button onClick={() => openEditor(blank)} data-testid="hero-add" className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25]">
          <Plus className="w-4 h-4" /> Nuevo slide
        </button>
      </div>

      <div className="grid gap-4" data-testid="hero-list">
        {slides.map((s, idx) => (
          <div
            key={s.id}
            draggable
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver(idx)}
            onDragLeave={onDragLeave}
            onDrop={onDrop(idx)}
            className={`bg-white border p-5 flex items-center gap-5 cursor-move transition-all ${
              dragOver === idx ? "border-[#C8102E] shadow-md scale-[1.005]" : "border-gray-200"
            }`}
            data-testid={`hero-row-${s.id}`}
          >
            <GripVertical className="w-5 h-5 text-gray-300 shrink-0" />
            {s.image_url && (
              <img src={s.image_url} alt="" className="w-40 h-24 object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 uppercase tracking-wider">#{s.order}</span>
                {s.active ? (
                  <span className="text-xs text-green-700">● Activo</span>
                ) : (
                  <span className="text-xs text-gray-400">○ Inactivo</span>
                )}
              </div>
              <h3 className="font-medium text-[#0F2040] truncate">{s.title_es}</h3>
              <p className="text-xs text-gray-500 italic truncate">{s.title_fr}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEditor(s)} className="px-3 py-2 text-xs border border-gray-200 hover:border-[#0F2040]">Editar</button>
              <button onClick={() => remove(s.id)} className="p-2 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="hero-editor">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="font-semibold text-[#0F2040]">{editing.id ? "Editar slide" : "Nuevo slide"}</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <ImageUpload
                label="Imagen del slide *"
                aspect="video"
                value={editing.image_url}
                onChange={(v) => setEditing((prev) => ({ ...prev, image_url: v }))}
                help="Recomendado: imagen panorámica de alta calidad (1920×1080 o superior)."
              />

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="CTA URL" value={editing.cta_url} onChange={(v) => setEditing((prev) => ({ ...prev, cta_url: v }))} />
                <Field label="Orden" type="number" value={editing.order} onChange={(v) => setEditing((prev) => ({ ...prev, order: parseInt(v || "0") }))} />
              </div>

              {/* Language tabs for title / subtitle / CTA label */}
              <div className="border border-gray-200">
                <div className="border-b border-gray-200 flex items-center gap-1 flex-wrap bg-gray-50" data-testid="hero-lang-tabs">
                  {LANG_TABS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setTab(l.code)}
                      data-testid={`hero-tab-${l.code}`}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        tab === l.code
                          ? "border-[#C8102E] text-[#0F2040] bg-white"
                          : "border-transparent text-gray-500 hover:text-[#0F2040]"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                  {tab !== "es" && editing.title_es && (
                    <button
                      type="button"
                      onClick={aiTranslateCurrent}
                      disabled={translating}
                      title={`Rellenar título, subtítulo y CTA en ${tab.toUpperCase()} traduciendo desde ES con IA`}
                      data-testid="hero-ai-translate"
                      className="ml-auto mr-2 my-1 inline-flex items-center gap-1.5 text-xs text-[#C8102E] border border-[#C8102E]/40 px-3 py-1.5 hover:bg-[#C8102E] hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {translating ? "Traduciendo…" : `Traducir con IA → ${tab.toUpperCase()}`}
                    </button>
                  )}
                </div>
                <div className="p-4 space-y-3" dir={tab === "ar" ? "rtl" : "ltr"}>
                  <Field
                    label={`Título (${tab.toUpperCase()})${tab === "es" ? " *" : ""}`}
                    value={editing[`title_${tab}`]}
                    onChange={(v) => setEditing((prev) => ({ ...prev, [`title_${tab}`]: v }))}
                    testid={`hero-title-${tab}`}
                  />
                  <Field
                    label={`Subtítulo (${tab.toUpperCase()})`}
                    value={editing[`subtitle_${tab}`]}
                    onChange={(v) => setEditing((prev) => ({ ...prev, [`subtitle_${tab}`]: v }))}
                    textarea
                    testid={`hero-subtitle-${tab}`}
                  />
                  <Field
                    label={`CTA Etiqueta (${tab.toUpperCase()})`}
                    value={editing[`cta_label_${tab}`]}
                    onChange={(v) => setEditing((prev) => ({ ...prev, [`cta_label_${tab}`]: v }))}
                    testid={`hero-cta-${tab}`}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing((prev) => ({ ...prev, active: e.target.checked }))} className="accent-[#C8102E]" />
                Slide activo (visible en la web)
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button onClick={save} data-testid="hero-save" className="bg-[#0F2040] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#1A2F5C]">
                  <Save className="w-4 h-4" /> Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" />
      )}
    </div>
  );
}
